import { useState } from 'react';
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Chip,
  Container,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Toolbar,
} from '@mui/material';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { ColorModeToggle } from '@/components/ColorModeToggle';
import { PageTransition } from '@/components/PageTransition';
import { currentPersona, usePersonas } from '@/personas/api';
import { useTenant } from '@/tenant/useTenant';
import { AccountMenu } from './AccountMenu';
import { activeNavItem, useNavItems } from './navigation';

/** Bottom navigation fits 4 destinations + "Mais" comfortably on phones. */
const MOBILE_SLOTS = 4;

export function AppLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isPlatform } = useTenant();
  const items = useNavItems();
  const current = activeNavItem(items, pathname);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const personas = usePersonas();
  const persona = currentPersona(personas.data);

  const overflow = items.length > MOBILE_SLOTS + 1;
  const mobileItems = overflow ? items.slice(0, MOBILE_SLOTS) : items;
  const moreItems = overflow ? items.slice(MOBILE_SLOTS) : [];
  const moreActive = moreItems.some((item) => item.to === current);

  return (
    <Box sx={{ minHeight: '100dvh', pb: { xs: 11, md: 0 } }}>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: 3, minHeight: { xs: 60, sm: 64 } }}>
          <NavLink
            to="/"
            style={{ textDecoration: 'none', color: 'inherit', minWidth: 0 }}
            aria-label="Início"
          >
            <BrandLogo size="small" />
          </NavLink>
          {isPlatform && (
            <Chip
              size="small"
              variant="soft"
              color="accent"
              label="Console"
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            />
          )}
          {!isPlatform && personas.data?.enabled && (
            <Chip
              size="small"
              variant="soft"
              color="accent"
              icon={<SwapHorizRounded />}
              label={persona?.label ?? 'Todos os papéis'}
              onClick={() => void navigate('/perfis')}
              aria-label="Trocar perfil"
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
            />
          )}
          <Tabs
            value={current}
            component="nav"
            aria-label="Navegação principal"
            variant="scrollable"
            scrollButtons={false}
            sx={{
              display: { xs: 'none', md: 'flex' },
              minHeight: 64,
              '& .MuiTab-root': { minHeight: 64 },
            }}
          >
            {items.map((item) => (
              <Tab
                key={item.to}
                value={item.to}
                label={item.label}
                component={NavLink}
                to={item.to}
              />
            ))}
          </Tabs>
          <Stack direction="row" spacing={0.5} sx={{ ml: 'auto', alignItems: 'center' }}>
            <ColorModeToggle />
            <AccountMenu />
          </Stack>
        </Toolbar>
      </AppBar>

      <Container component="main" maxWidth="lg" sx={{ pt: { xs: 3, md: 4 }, pb: 10 }}>
        <PageTransition>
          <Outlet />
        </PageTransition>
      </Container>

      <Paper
        component="nav"
        aria-label="Navegação principal"
        sx={{
          display: { md: 'none' },
          position: 'fixed',
          insetInline: 0,
          bottom: 0,
          zIndex: 'appBar',
          borderRadius: 0,
        }}
      >
        <BottomNavigation value={moreActive ? 'more' : current} showLabels>
          {mobileItems.map((item) => (
            <BottomNavigationAction
              key={item.to}
              value={item.to}
              label={item.label}
              icon={<item.icon />}
              component={NavLink}
              to={item.to}
            />
          ))}
          {overflow && (
            <BottomNavigationAction
              value="more"
              label="Mais"
              icon={<MoreHorizRounded />}
              aria-haspopup="menu"
              onClick={(event) => setMoreAnchor(event.currentTarget)}
            />
          )}
        </BottomNavigation>
      </Paper>
      <Menu
        anchorEl={moreAnchor}
        open={Boolean(moreAnchor)}
        onClose={() => setMoreAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {moreItems.map((item) => (
          <MenuItem
            key={item.to}
            selected={item.to === current}
            onClick={() => {
              setMoreAnchor(null);
              void navigate(item.to);
            }}
          >
            <ListItemIcon>
              <item.icon fontSize="small" />
            </ListItemIcon>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
